// The side of the customer screen: the customer's appointments and "Book
// appointment" (day, start, length, who with, a note), their follow-ups, "Add
// a follow-up" (reason, when, a note for whoever calls), and their notes and
// wishes, shared by every store. A wish can name a product by its barcode or
// SKU, or just describe it. Online only, like the rest of the customer's
// history. Messages go through the screen's one toast (say).
import React, { useEffect, useRef, useState } from "react";
import { fashionPosService } from "../../../services";
import { readMessage } from "../../../utils/fashion/holds";
import { productForCode } from "../../../utils/fashion/markdowns";
import {
  FOLLOW_UP_REASONS,
  FOLLOW_UP_WHEN,
  KIND_LABEL,
  MAX_CLIENT_TEXT,
  buildAddClientItemMutation,
  buildClientBookQuery,
  buildCloseClientItemMutation,
  canMarkDone,
  daysOverdue,
  dueLabel,
  followUpDue,
  followUpText,
  itemTitle,
  readClientItems,
  splitFollowUpText,
  validateClientDraft,
} from "../../../utils/fashion/clientBook";
import {
  APPOINTMENT_LENGTHS,
  DEFAULT_LENGTH,
  buildAddAppointmentMutation,
  buildStoreStaffQuery,
  lastAppointmentDay,
  nextSlot,
  readStaff,
  upcomingAppointments,
  validateAppointmentDraft,
  whenLabel,
} from "../../../utils/fashion/appointments";
import EmptyState from "./EmptyState";

const NOTE_KINDS = ["NOTE", "WISH"];
const PLACEHOLDER = { NOTE: "Prefers slim fit, doesn't wear wool…", WISH: "What they're after, if it isn't on the till" };
const firstName = (name) => String(name || "").trim().split(/\s+/)[0] || "this customer";

// Overdue is bad, today is a warning, later is information.
const dueTone = (dueDate) => {
  const d = daysOverdue(dueDate);
  if (d == null) return "neu";
  if (d > 0) return "bad";
  return d === 0 ? "warn" : "info";
};

export default function FashionClientBook({ customer, bunitId, products, say = () => {} }) {
  const customerId = customer?.cwrCustomerId;
  const [book, setBook] = useState({ status: "loading", rows: [] });
  const [fup, setFup] = useState({ reason: FOLLOW_UP_REASONS[0], when: FOLLOW_UP_WHEN[0].label, note: "" });
  const [noteDraft, setNoteDraft] = useState({ kind: "NOTE", text: "", code: "" });
  const [appt, setAppt] = useState(null); // the open "Book appointment" form, or null
  const [staff, setStaff] = useState([]); // who an appointment can be with; none when the read fails
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!customerId) return undefined;
    let live = true;
    setBook((b) => ({ ...b, status: "loading" }));
    (async () => {
      let rows = null;
      try {
        rows = readClientItems(await fashionPosService(buildClientBookQuery(customerId)), "getFashionClientBook");
      } catch {
        rows = null;
      }
      if (live) setBook(rows === null ? { status: "error", rows: [] } : { status: "done", rows });
    })();
    return () => {
      live = false;
    };
  }, [customerId, reload]);

  // The store's staff, read once the book form is first opened.
  const wantStaff = appt !== null;
  useEffect(() => {
    if (!wantStaff || !bunitId) return undefined;
    let live = true;
    (async () => {
      try {
        const rows = readStaff(await fashionPosService(buildStoreStaffQuery(bunitId)));
        if (live && rows) setStaff(rows);
      } catch {
        // Booking still works without a staff member.
      }
    })();
    return () => {
      live = false;
    };
  }, [wantStaff, bunitId]);

  // One call at a time: state lags a fast double tap, the ref doesn't.
  const run = async (mutation, field, fallback, done) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const res = readMessage(await fashionPosService(mutation), field, fallback);
      if (res.ok) {
        say(done?.() || res.message || "Saved.", "ok");
        setReload((n) => n + 1);
      } else say(res.message, "bad");
    } catch {
      say(`${fallback} Check the connection and try again.`, "bad");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const addFollowUp = (e) => {
    e.preventDefault();
    if (!fup.note.trim()) return;
    const { item, error } = validateClientDraft({ kind: "TASK", text: followUpText(fup.reason, fup.note), dueDate: followUpDue(fup.when), bunitId, customerId });
    if (error) {
      say(error, "warn");
      return;
    }
    const when = fup.when;
    run(buildAddClientItemMutation(item), "addFashionClientItem", "Couldn't add the follow-up.", () => {
      setFup((f) => ({ ...f, note: "" }));
      return `Follow-up added for ${customer?.name || "the customer"} · ${when.toLowerCase()}`;
    });
  };

  const addNote = (e) => {
    e.preventDefault();
    let productId = null;
    const code = noteDraft.code.trim();
    if (noteDraft.kind === "WISH" && code) {
      const p = productForCode(products, code);
      if (!p) {
        say(`Nothing on this till matches ${code}. Describe it instead, or check the code.`, "warn");
        return;
      }
      productId = p.mProductId;
    }
    const { item, error } = validateClientDraft({ ...noteDraft, productId, bunitId, customerId });
    if (error) {
      say(error, "warn");
      return;
    }
    run(buildAddClientItemMutation(item), "addFashionClientItem", "Couldn't save it.", () => {
      setNoteDraft((d) => ({ kind: d.kind, text: "", code: "" }));
      return `${KIND_LABEL[item.kind]} saved for ${customer?.name || "the customer"}.`;
    });
  };

  const openBooking = () => {
    const { day, time } = nextSlot();
    setAppt({ day, time, minutes: DEFAULT_LENGTH, staffId: "", note: "" });
  };

  const bookAppointment = (e) => {
    e.preventDefault();
    const { item, error } = validateAppointmentDraft({ ...appt, bunitId, customerId });
    if (error) {
      say(error, "warn");
      return;
    }
    run(buildAddAppointmentMutation(item), "addFashionClientItem", "Couldn't book the appointment.", () => {
      setAppt(null);
      const who = staff.find((s) => s.id === item.staffId);
      return `Appointment booked for ${customer?.name || "the customer"} · ${whenLabel(item.startAt)}${who ? ` with ${who.name}` : ""}`;
    });
  };

  const close = (item, status) => run(buildCloseClientItemMutation(item.id, status), "closeFashionClientItem", "Couldn't update it.");

  const open = book.rows.filter((r) => r.status === "OPEN");
  const tasks = open.filter((r) => r.kind === "TASK").sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)));
  const notes = open.filter((r) => r.kind === "NOTE" || r.kind === "WISH");
  const appointments = upcomingAppointments(book.rows);

  const actions = (item) => (
    <span className="fp-cust-row">
      {canMarkDone(item) && (
        <button type="button" className="fp-cust-btn is-sm" onClick={() => close(item, "DONE")} disabled={busy}>
          Done
        </button>
      )}
      <button type="button" className="fp-cust-btn is-sm" onClick={() => close(item, "CAN")} disabled={busy} aria-label={`Remove ${itemTitle(item)}`}>
        Remove
      </button>
    </span>
  );

  return (
    <div className="fp-cust-book" aria-label="Follow-ups for this customer">
      <h2>Appointments</h2>
      {book.status === "done" && appointments.length === 0 && <p className="fp-cust-hint">No appointments booked.</p>}
      {appointments.length > 0 && (
        <ul className="fp-cust-list" aria-label="Booked appointments">
          {appointments.map((item) => (
            <li key={item.id} className="fp-cust-card">
              <div className="fp-cust-row">
                <b className="fp-cust-grow">{whenLabel(item.startAt)}</b>
                {item.endAt && <span className="fp-cust-pill is-info">{`until ${item.endAt.slice(11, 16)}`}</span>}
              </div>
              {(item.staffName || item.text) && <span className="fp-cust-hint">{[item.staffName ? `With ${item.staffName}` : "", item.text].filter(Boolean).join(" · ")}</span>}
              {item.storeName && <span className="fp-cust-hint">{item.storeName}</span>}
              <span className="fp-cust-row">
                <button type="button" className="fp-cust-btn is-sm" onClick={() => close(item, "CAN")} disabled={busy} aria-label={`Cancel the appointment ${whenLabel(item.startAt)}`}>
                  Cancel
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {appt ? (
        <form className="fp-cust-card" onSubmit={bookAppointment} aria-label="Book appointment">
          <b>Book an appointment</b>
          <div className="fp-cust-row">
            <label className="fp-cust-field fp-cust-grow">
              <span className="fp-cust-lbl">Day</span>
              <input type="date" className="fp-cust-input" value={appt.day} min={nextSlot().day} max={lastAppointmentDay()} onChange={(e) => setAppt((a) => ({ ...a, day: e.target.value }))} />
            </label>
            <label className="fp-cust-field fp-cust-grow">
              <span className="fp-cust-lbl">Start</span>
              <input type="time" className="fp-cust-input" value={appt.time} step={300} onChange={(e) => setAppt((a) => ({ ...a, time: e.target.value }))} />
            </label>
          </div>
          <div className="fp-cust-field">
            <span className="fp-cust-lbl" id="fp-appt-len">
              Length
            </span>
            <div className="fp-cust-seg" role="group" aria-labelledby="fp-appt-len">
              {APPOINTMENT_LENGTHS.map((m) => (
                <button key={m} type="button" className={appt.minutes === m ? "is-on" : ""} aria-pressed={appt.minutes === m} onClick={() => setAppt((a) => ({ ...a, minutes: m }))}>
                  {`${m} min`}
                </button>
              ))}
            </div>
          </div>
          <label className="fp-cust-field">
            <span className="fp-cust-lbl">With (optional)</span>
            <select className="fp-cust-input" value={appt.staffId} onChange={(e) => setAppt((a) => ({ ...a, staffId: e.target.value }))}>
              <option value="">Anyone free</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="fp-cust-field">
            <span className="fp-cust-lbl">Note (optional)</span>
            <input
              className="fp-cust-input"
              value={appt.note}
              maxLength={MAX_CLIENT_TEXT}
              onChange={(e) => setAppt((a) => ({ ...a, note: e.target.value }))}
              placeholder="e.g. Bridal fitting, bring the blouse"
            />
          </label>
          <span className="fp-cust-row">
            <button type="submit" className="fp-cust-btn is-pri" disabled={busy}>
              {busy ? "Booking…" : "Book appointment"}
            </button>
            <button type="button" className="fp-cust-btn" onClick={() => setAppt(null)} disabled={busy}>
              Not now
            </button>
          </span>
        </form>
      ) : (
        <button type="button" className="fp-cust-btn" onClick={openBooking}>
          Book appointment
        </button>
      )}

      <h2>Follow-ups</h2>
      {book.status === "error" ? (
        <p className="fp-cust-hint" role="alert">
          Couldn't load this customer's follow-ups.
        </p>
      ) : book.status === "loading" && book.rows.length === 0 ? (
        <p className="fp-cust-hint">Loading follow-ups…</p>
      ) : tasks.length === 0 ? (
        <EmptyState compact title={`Nothing planned for ${firstName(customer?.name)}.`} />
      ) : (
        <ul className="fp-cust-list" aria-label="Open follow-ups">
          {tasks.map((item) => {
            const { reason, note } = splitFollowUpText(item.text);
            return (
              <li key={item.id} className="fp-cust-card">
                <div className="fp-cust-row">
                  <b className="fp-cust-grow">{reason || note}</b>
                  <span className={`fp-cust-pill is-${dueTone(item.dueDate)}`}>{dueLabel(item.dueDate)}</span>
                </div>
                {reason && note && <span className="fp-cust-hint">{note}</span>}
                {item.storeName && <span className="fp-cust-hint">{item.storeName}</span>}
                {actions(item)}
              </li>
            );
          })}
        </ul>
      )}

      <form className="fp-cust-card" onSubmit={addFollowUp} aria-label="Add follow-up">
        <b>Add a follow-up</b>
        <div className="fp-cust-field">
          <span className="fp-cust-lbl" id="fp-fup-reason">
            Reason
          </span>
          <div className="fp-cust-seg" role="group" aria-labelledby="fp-fup-reason">
            {FOLLOW_UP_REASONS.map((r) => (
              <button key={r} type="button" className={fup.reason === r ? "is-on" : ""} aria-pressed={fup.reason === r} onClick={() => setFup((f) => ({ ...f, reason: r }))}>
                {r}
              </button>
            ))}
          </div>
        </div>
        <div className="fp-cust-field">
          <span className="fp-cust-lbl" id="fp-fup-when">
            When
          </span>
          <div className="fp-cust-seg" role="group" aria-labelledby="fp-fup-when">
            {FOLLOW_UP_WHEN.map((w) => (
              <button key={w.label} type="button" className={fup.when === w.label ? "is-on" : ""} aria-pressed={fup.when === w.label} onClick={() => setFup((f) => ({ ...f, when: w.label }))}>
                {w.label}
              </button>
            ))}
          </div>
        </div>
        <label className="fp-cust-field">
          <span className="fp-cust-lbl">Note for whoever calls</span>
          <textarea
            className="fp-cust-input"
            value={fup.note}
            maxLength={MAX_CLIENT_TEXT - fup.reason.length - 3}
            onChange={(e) => setFup((f) => ({ ...f, note: e.target.value }))}
            placeholder="e.g. Tell them the Rust wrap dress in M is back"
          />
        </label>
        <button type="submit" className="fp-cust-btn is-pri" disabled={busy || !fup.note.trim()}>
          {busy ? "Saving…" : "Add follow-up"}
        </button>
      </form>

      <h3>Notes and wishes</h3>
      {book.status === "done" && notes.length === 0 && <p className="fp-cust-hint">No notes or wishes yet.</p>}
      {notes.length > 0 && (
        <ul className="fp-cust-list" aria-label="Notes and wishes">
          {notes.map((item) => (
            <li key={item.id} className="fp-cust-card">
              <div className="fp-cust-row">
                <span className="fp-cust-pill">{KIND_LABEL[item.kind] || item.kind}</span>
                <span className="fp-cust-grow">{itemTitle(item)}</span>
              </div>
              {(item.kind === "WISH" && item.productName && item.text) || item.storeName ? (
                <span className="fp-cust-hint">{[item.kind === "WISH" && item.productName ? item.text : null, item.storeName].filter(Boolean).join(" · ")}</span>
              ) : null}
              {actions(item)}
            </li>
          ))}
        </ul>
      )}
      <form className="fp-cust-card" onSubmit={addNote} aria-label="Add a note or wish">
        <div className="fp-cust-seg" role="group" aria-label="Kind">
          {NOTE_KINDS.map((k) => (
            <button key={k} type="button" className={noteDraft.kind === k ? "is-on" : ""} aria-pressed={noteDraft.kind === k} onClick={() => setNoteDraft((d) => ({ ...d, kind: k }))}>
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>
        <input
          className="fp-cust-input"
          value={noteDraft.text}
          onChange={(e) => setNoteDraft((d) => ({ ...d, text: e.target.value }))}
          maxLength={MAX_CLIENT_TEXT}
          placeholder={PLACEHOLDER[noteDraft.kind]}
          aria-label={KIND_LABEL[noteDraft.kind]}
        />
        {noteDraft.kind === "WISH" && (
          <input
            className="fp-cust-input"
            value={noteDraft.code}
            onChange={(e) => setNoteDraft((d) => ({ ...d, code: e.target.value }))}
            placeholder="Barcode or SKU (optional)"
            aria-label="Wished product barcode or SKU"
            autoComplete="off"
          />
        )}
        <button type="submit" className="fp-cust-btn" disabled={busy}>
          {`Add ${KIND_LABEL[noteDraft.kind].toLowerCase()}`}
        </button>
      </form>
    </div>
  );
}
